import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Pagination — كمبوننت مشترك لكل صفحات الداشبورد
 * Props:
 *   meta        — { current_page, last_page }
 *   currentPage — رقم الصفحة الحالية
 *   loading     — boolean
 *   onChange    — fn(page)
 *   marginTop   — رقم (افتراضي 28)
 */
const Pagination = ({ meta, currentPage, loading, onChange, marginTop = 28 }) => {
  const { t, i18n } = useTranslation("global");
  
  if (!meta) return null;
  const lastPage = meta.last_page ?? meta.lastPage ?? 1;
  if (lastPage <= 1) return null;

  // بناء قائمة الأرقام: أول + نقاط + وسط + نقاط + آخر
  const buildPages = () => {
    const delta = 2;
    const pages = [];
    const range = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(lastPage - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    pages.push(1);
    if (range.length > 0 && range[0] > 2) pages.push("...-left");
    range.forEach((p) => pages.push(p));
    if (range.length > 0 && range[range.length - 1] < lastPage - 1) pages.push("...-right");
    if (lastPage > 1) pages.push(lastPage);

    return pages;
  };

  const pages = buildPages();

  const btnBase = {
    height: 38,
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.18s",
    border: "none",
    outline: "none",
  };

  const activeStyle = {
    ...btnBase,
    width: 38,
    background: "linear-gradient(135deg, var(--main-color), var(--main-light))",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(99,102,241,0.25)",
    cursor: "default",
  };

  const inactiveStyle = {
    ...btnBase,
    width: 38,
    background: "#fff",
    color: "#475569",
    border: "1px solid #e2e8f0",
    cursor: loading ? "not-allowed" : "pointer",
  };

  const navStyle = (disabled) => ({
    ...btnBase,
    padding: "0 18px",
    background: "#fff",
    color: disabled ? "#cbd5e1" : "#475569",
    border: "1px solid #e2e8f0",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600,
    fontSize: 13,
  });

  const dotsStyle = {
    height: 38,
    width: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    fontSize: 14,
    fontWeight: 700,
    userSelect: "none",
  };

  const isRtl = i18n.language === "ar";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 6,
        marginTop,
        direction: isRtl ? "rtl" : "ltr",
      }}
    >
      {/* زر السابق - يظهر في اليمين في الـ RTL وفي اليسار في الـ LTR */}
      <button
        style={navStyle(currentPage === 1 || loading)}
        onClick={() => currentPage > 1 && !loading && onChange(currentPage - 1)}
        disabled={currentPage === 1 || loading}
      >
        {t("btns.prev")}
      </button>

      {/* أرقام الصفحات */}
      <div style={{ display: "flex", gap: 5, alignItems: "center", direction: isRtl ? "rtl" : "ltr" }}>
        {pages.map((p, idx) => {
          if (typeof p === "string") {
            return (
              <span key={p + idx} style={dotsStyle}>
                ···
              </span>
            );
          }
          const isActive = p === currentPage;
          return (
            <button
              key={p}
              style={isActive ? activeStyle : inactiveStyle}
              onClick={() => !isActive && !loading && onChange(p)}
              disabled={loading}
            >
              {p}
            </button>
          );
        })}
      </div>

      {/* زر التالي */}
      <button
        style={navStyle(currentPage === lastPage || loading)}
        onClick={() => currentPage < lastPage && !loading && onChange(currentPage + 1)}
        disabled={currentPage === lastPage || loading}
      >
        {t("btns.next")}
      </button>
    </div>
  );
};

export default Pagination;
